import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faMagnifyingGlass, faPlus, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./OrganizationsAdminModal.module.css";
import sheetStyles from "./OrganizationModal.module.css";
import { Sheet } from "./OrganizationModal";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import {
  describeOrgBilling, formatDateBR, formatPlanPrice, INTERVAL_LABELS, INTERVAL_OPTIONS, ORG_BILLING,
} from "../utils/orgBilling";

// Lista de academias para o admin da plataforma (GET /admin/organizations).
// Criar é pelo convite (tipo Academia) ou pela conversão na edição do
// professor; editar/desfazer fica com o suporte. A cobrança de cada academia
// (preço exclusivo, "pago até" manual ou cortesia) é definida aqui, no
// BillingSheet (PUT /admin/organizations/{id}/billing).
// `reloadKey` muda quando o convite de academia fecha, pra lista refletir a
// academia recém-criada sem o admin precisar atualizar.

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

const BILLING_PILL = { ok: "pillOk", warn: "pillWarn", danger: "pillDanger", neutral: "pillOff" };

// "490,00", "1.200,50" ou "490.5" -> número. Vírgula = decimal brasileiro
// (pontos são milhar); sem vírgula, ponto é decimal.
function parseAmount(text) {
  const t = (text || "").trim();
  if (!t) return null;
  const n = t.includes(",") ? Number(t.replace(/\./g, "").replace(",", ".")) : Number(t);
  return Number.isFinite(n) ? n : NaN;
}

function BillingSheet({ org, onClose, onSaved }) {
  const b = org.billing || {};
  const [amount, setAmount] = useState(b.plan ? String(b.plan.amount).replace(".", ",") : "");
  const [interval, setIntervalValue] = useState(b.plan?.interval || "monthly");
  const [paidUntil, setPaidUntil] = useState(b.paid_until || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmCourtesy, setConfirmCourtesy] = useState(false);

  const parsed = parseAmount(amount);
  const amountInvalid = amount.trim() !== "" && !(parsed > 0);
  const info = describeOrgBilling(b, { audience: "admin" });

  const send = async (body) => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/admin/organizations/${org.id}/billing`, body);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível salvar a cobrança.");
      setBusy(false);
    }
  };

  const save = (e) => {
    e.preventDefault();
    if (amountInvalid) return;
    const body = { interval, paid_until: paidUntil || null };
    if (parsed) body.amount = parsed;
    send(body);
  };

  return (
    <Sheet title={`Cobrança: ${org.name}`} labelId="org-billing-title" onClose={onClose} busy={busy}>
      {info && (
        <p className={sheetStyles.sheetText}>
          <span className={sheetStyles.strong}>{info.title}.</span> {info.detail}
        </p>
      )}
      <form className={sheetStyles.form} onSubmit={save} noValidate>
        <div className={sheetStyles.field}>
          <label htmlFor="org-billing-amount">Valor da assinatura (R$)</label>
          <input
            id="org-billing-amount"
            className={sheetStyles.input}
            inputMode="decimal"
            placeholder="Ex.: 490,00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-invalid={amountInvalid}
            aria-describedby="org-billing-amount-hint"
          />
          <p id="org-billing-amount-hint" className={sheetStyles.hint}>
            {amountInvalid
              ? "Informe um valor maior que zero."
              : "O dono assina pelo app com Pix ou cartão. Mudar o valor vale para a próxima assinatura; a atual segue no valor antigo até ser cancelada."}
          </p>
        </div>
        <div className={sheetStyles.field}>
          <label htmlFor="org-billing-interval">Periodicidade</label>
          <select
            id="org-billing-interval"
            className={sheetStyles.input}
            value={interval}
            onChange={(e) => setIntervalValue(e.target.value)}
          >
            {INTERVAL_OPTIONS.map((k) => <option key={k} value={k}>{INTERVAL_LABELS[k]}</option>)}
          </select>
        </div>
        <div className={sheetStyles.field}>
          <label htmlFor="org-billing-paid-until">Pago até (opcional)</label>
          <input
            id="org-billing-paid-until"
            className={sheetStyles.input}
            type="date"
            value={paidUntil}
            onChange={(e) => setPaidUntil(e.target.value)}
            aria-describedby="org-billing-paid-until-hint"
          />
          <p id="org-billing-paid-until-hint" className={sheetStyles.hint}>
            Para contrato fechado fora do app (boleto, transferência) ou piloto. Vale o que vencer por último entre esta data e a assinatura.
          </p>
        </div>
        {error && <p className={sheetStyles.error} role="alert">{error}</p>}
        <div className={sheetStyles.sheetActions}>
          <button type="button" className={sheetStyles.btn} onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className={`${sheetStyles.btn} ${sheetStyles.btnPrimary}`} disabled={busy || amountInvalid}>
            {busy ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
      {b.status && b.status !== ORG_BILLING.COURTESY && (
        confirmCourtesy ? (
          <div className={sheetStyles.sheetActions}>
            <button type="button" className={sheetStyles.btn} onClick={() => setConfirmCourtesy(false)} disabled={busy}>Voltar</button>
            <button type="button" className={`${sheetStyles.btn} ${sheetStyles.btnDanger}`} onClick={() => send({ courtesy: true })} disabled={busy}>
              Confirmar cortesia
            </button>
          </div>
        ) : (
          <button type="button" className={sheetStyles.linkBtn} onClick={() => setConfirmCourtesy(true)} disabled={busy}>
            Voltar para cortesia (sem cobrança)
          </button>
        )
      )}
    </Sheet>
  );
}

export default function OrganizationsAdminModal({ open, onClose, onNewOrganization, reloadKey }) {
  useBodyScrollLock(open);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [billingFor, setBillingFor] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/organizations", { params: query ? { q: query } : {} });
      setOrgs(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível carregar as academias.");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (open) load();
  }, [open, load, reloadKey]);

  if (!open) return null;

  return (
    <div className={styles.overlay} role="presentation">
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="orgs-admin-title">
        <ModalHeader onClose={onClose} />
        <div className={styles.content}>
          <div className={styles.titleRow}>
            <div>
              <h2 id="orgs-admin-title" className={styles.title}>Academias</h2>
              <div className={styles.sub}>
                {loading ? "Carregando..." : plural(orgs.length, "academia", "academias")}
              </div>
            </div>
            <div className={styles.titleActions}>
              <button type="button" className={styles.iconBtn} onClick={load} aria-label="Atualizar lista de academias" title="Atualizar">
                <FontAwesomeIcon icon={faRotateRight} spin={loading} />
              </button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNewOrganization}>
                <FontAwesomeIcon icon={faPlus} /> Nova academia
              </button>
            </div>
          </div>

          <div className={styles.search}>
            <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} aria-hidden="true" />
            <input
              id="orgs-admin-search"
              className={styles.searchInput}
              type="search"
              placeholder="Buscar academia..."
              aria-label="Buscar academia"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {error && <p className={styles.error} role="alert">{error}</p>}

          {!loading && !error && orgs.length === 0 && (
            <div className={styles.empty}>
              <FontAwesomeIcon icon={faBuilding} className={styles.emptyIcon} />
              <span>
                {query
                  ? "Nenhuma academia com esse nome."
                  : "Nenhuma academia ainda. Crie uma em Nova academia ou converta um professor na edição do usuário."}
              </span>
            </div>
          )}

          <ul className={styles.list}>
            {orgs.map((o) => (
              <li key={o.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <span className={styles.name}>{o.name}</span>
                  <span className={styles.date}>desde {fmtDate(o.created_at)}</span>
                </div>
                <div className={styles.owner}>
                  {o.owner ? (
                    <>
                      Dono: <strong>{o.owner.name}</strong> <span className={styles.email}>{o.owner.email}</span>
                      {!o.owner.is_active && <span className={`${styles.pill} ${styles.pillOff}`}>Inativo</span>}
                    </>
                  ) : (
                    <span className={`${styles.pill} ${styles.pillWarn}`}>Sem dono</span>
                  )}
                </div>
                <div className={styles.counts}>
                  <span className={styles.count}>{plural(o.professors_active, "professor", "professores")}</span>
                  <span className={styles.count}>{plural(o.students_active, "aluno ativo", "alunos ativos")}</span>
                  {o.students_unassigned > 0 && (
                    <span className={`${styles.count} ${styles.countWarn}`}>{o.students_unassigned} sem professor</span>
                  )}
                </div>
                {(() => {
                  const info = describeOrgBilling(o.billing, { audience: "admin" });
                  const extra = [
                    formatPlanPrice(o.billing?.plan),
                    o.billing?.paid_until && `pago até ${formatDateBR(o.billing.paid_until)}`,
                  ].filter(Boolean).join(" · ");
                  return (
                    <div className={styles.billingRow}>
                      {info && <span className={`${styles.pill} ${styles[BILLING_PILL[info.tone]] || ""}`}>{info.title}</span>}
                      <span className={styles.billingText}>{extra}</span>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSm}`}
                        onClick={() => setBillingFor(o)}
                        aria-label={`Cobrança da academia ${o.name}`}
                      >
                        Cobrança
                      </button>
                    </div>
                  );
                })()}
              </li>
            ))}
          </ul>
        </div>
        {billingFor && (
          <BillingSheet
            org={billingFor}
            onClose={() => setBillingFor(null)}
            onSaved={() => { setBillingFor(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}
